import { useQuery } from "@tanstack/react-query";

import { useAuth, useModuleVisible } from "@/contexts/AuthContext";
import { dashboardService } from "@/entities/dashboard";
import {
  groupByCluster,
  isCapabilityHiddenByBrand,
  presentationOf,
  PROMOTION_SOFTLINK_CAP,
  withPromotionSoftLink,
  useCapabilities,
  type CapabilityCard,
  type CapabilityCluster,
} from "@/entities/capability";

import { useQikeEnabled } from "@/features/acquisition/api/qikeService";
import { useBrand } from "@/shared/brand";
import { lapsedModulesOf } from "@/shared/lib/forbiddenReason";

import { DEPT_ENTRIES, type DeptEntry } from "./workbenchCatalog";

/**
 * 一个群该上墙的卡 + 计数 —— **首页横滑带 / 群页卡墙 共用单源**(2026-07-25 发现流改造)。
 *
 * ★为什么抽出来:首页(每群一行)和群页(单群一墙)是同一套「归组 + 门控 + 计数」逻辑,
 *   原来只活在 Index.tsx 里;群页要复用,不能把这坨 hook(useModuleVisible×7 + 两个 query)
 *   在两处各写一遍(必漂)。抽成一个 hook,两处都调 forCluster(cluster)。
 *
 * ★口径与旧 Index 一致:①内容群走 DEPT_ENTRIES(WorkbenchDeptCard,有主角图/积分标),
 *   其余群走 capability 卡;②dept-render 舱后端若也挂 cluster 不重复列(点不开的假卡);
 *   ③active 服务舱未开通不上墙(不给点了 403 的假承诺);④listModules status!=active 的 dept 不上。
 */
export interface ClusterCards {
  depts: DeptEntry[];
  /**
   * 1005:因到期关闭的内容舱(拥有过、现在关着、没被暂停)。此前直接不上墙 ⇒ 套餐到期后首页「内容生产」
   * 整块消失、毫无解释;现在照样上墙,标「已到期」、不可进入(与「即将」卡同一套占位态)。
   */
  expiredDepts?: DeptEntry[];
  caps: CapabilityCard[];
  counts: { active: number; integrating: number; coming: number };
}

export function useClusterCards({ enabled = true }: { enabled?: boolean } = {}) {
  const { data: modules, isLoading } = useQuery({
    queryKey: ["dashboard-modules"],
    queryFn: dashboardService.listModules,
    enabled,
  });
  const { data: capabilities } = useCapabilities();

  /* ─── 门控布尔(hooks 无条件调用):dept 5 舱 + 服务 4 舱 ─── */
  const gate: Record<string, boolean> = {
    ai_poster: useModuleVisible("ai_poster"),
    ai_video: useModuleVisible("ai_video"),
    ai_digital_human: useModuleVisible("ai_digital_human"),
    ai_explosive_parse: useModuleVisible("ai_explosive_parse"),
    ai_reporter: useModuleVisible("ai_reporter"),
    geo_radar: useModuleVisible("geo_radar"),
    ai_kefu: useModuleVisible("ai_kefu"),
    ai_peilian: useModuleVisible("ai_peilian"),
    // V25 军师回归:经营诊断有真 module 门(RbacMatrix 不给 STAFF)。漏登记 = `gate[cap] ?? true`
    // 兜底放行 → STAFF 也看见卡,点进去撞 routeRequiredModule 403(假卡,与「未开通 active 不上墙」同类)。
    biz_advisor: useModuleVisible("biz_advisor"),
    // 建站舱(2026-09-22 转正):有真 module 门。漏登记同上 —— 未开通的租户也见卡,点进去撞 403。
    ai_website: useModuleVisible("ai_website"),
    // 获客舱(OEM G2 演示机实锤):漏登记 → 基础版租户也见「已开」获客卡,点进去接口 403 MODULE_NOT_ENABLED。
    ai_acquisition: useModuleVisible("ai_acquisition"),
  };

  // 视图角色(代客态 = TENANT_ADMIN,AuthContext 单源)—— 给无 module 门的 meta 舱
  // (经营看板 gated=0,后端不发 entitlement)判卡的可见性用。
  const { effectiveRole } = useAuth();
  // 1028 拓客工作台:启客不是套餐模块(没有 module 门可判),可见性只看平台配没配 QIKE_*。
  // 未配置时不上墙 —— 否则是一张点进去「还没开通」的死卡。
  const qikeEnabled = useQikeEnabled();
  // OEM:外链独立站类舱(漫剧工坊)随品牌模块开关(§2.4)。
  const brand = useBrand();

  // 模块被明确停用(listModules status!=active)时不上部门卡。
  const disabledCodes = new Set(
    (modules ?? []).filter((m) => m.status !== "active").map((m) => m.code)
  );
  /**
   * OEM G2:/me/modules 列的是 catalog 全部计费产品模块(按租户权益标 active / disabled)= 有开通门的舱。
   * OEM H1 起 /me/permissions(gate 的来源)已是后端「角色 ∩ 租户开通」—— 侧栏 / ⌘K / 卡墙的主判据都是它;
   * 这里对 /me/modules 的复核降为纵深防御(两份数据刷新时刻不同,取更严的那份):
   *   ① 已标 disabled → 不上墙;② gate 表漏登记(下一个新舱)→ 按未开通处理,不再 `?? true` 放行。
   * 非计费舱(meta / 外链,gated=0,不在清单里)保持原口径。
   */
  const productCodes = new Set((modules ?? []).map((m) => m.code));
  const capOpen = (capability: string): boolean => {
    if (productCodes.has(capability)) {
      return (gate[capability] ?? false) && !disabledCodes.has(capability);
    }
    return gate[capability] ?? true;
  };
  /**
   * 一个模块「真正可用」= 有权限 且 未被停用。★首页 hero 快捷 chip / 在途线 / 部门卡 三处同口径,
   * 避免 chip 只查 useModuleVisible 不查 disabled 造成「chip 显示但卡因停用不上」的不一致(旧 P3)。
   */
  const moduleReady = (moduleKey: string): boolean =>
    (gate[moduleKey] ?? false) && !disabledCodes.has(moduleKey);
  const departments = DEPT_ENTRIES.filter((d) => moduleReady(d.moduleKey));
  const { permissions } = useAuth();
  const lapsed = new Set(lapsedModulesOf(permissions));
  const expiredDepartments = DEPT_ENTRIES.filter(
    (d) => !moduleReady(d.moduleKey) && lapsed.has(d.moduleKey)
  );

  // 2026-08-09 起卡墙全量走后端 catalog(此前两张「即将」卡写死在这里过渡;
  // ai_promotion 挂仓、ai_official_media 落库后,LOCAL_SOON_CARDS 让位退场)。
  const grouped = groupByCluster(capabilities);
  const visibleCaps = (cluster: CapabilityCluster): CapabilityCard[] =>
    (grouped.get(cluster) ?? [])
      .map((c) => withPromotionSoftLink(c, brand))
      .filter((c) => {
        const pres = presentationOf(c.capability);
        if (!pres || pres.render === "dept") return false; // dept 舱由 DEPT_ENTRIES 渲染,不重复
        // 推广舱软链(1012):没有 module 门可判(后端仍 coming_soon),开没开通由 GEO 页自己的接口说。
        const softLinked = c.capability === PROMOTION_SOFTLINK_CAP;
        if (c.status === "active" && !softLinked && !capOpen(c.capability)) return false; // 未开通 active 不上
        // meta 舱的角色白名单(无 module 门可判时的唯一门):角色未知先不上,别抢在权限到位前给假承诺。
        if (pres.roles && !(effectiveRole && pres.roles.includes(effectiveRole))) return false;
        if (isCapabilityHiddenByBrand(c.capability, brand)) return false;
        if (c.capability === "qike_workbench" && !qikeEnabled) return false;
        return true;
      });

  const forCluster = (cluster: CapabilityCluster): ClusterCards => {
    const depts = cluster === "content" ? departments : [];
    const expiredDepts = cluster === "content" ? expiredDepartments : [];
    const caps = visibleCaps(cluster);
    return {
      depts,
      expiredDepts,
      caps,
      counts: {
        active: depts.length + caps.filter((c) => c.status === "active").length,
        integrating: caps.filter((c) => c.status === "integrating").length,
        coming: caps.filter((c) => c.status === "coming_soon").length,
      },
    };
  };

  // visible = 纯权限(不含 disabled):在途/最近对停用模块的历史产物仍展示,与 moduleReady(创作入口)区分。
  return { isLoading, forCluster, moduleReady, visible: gate };
}
